import { AppData, Log } from "@lightningjs/sdk";
import { AppSetup } from "./app-setup";
import { Logger } from "./logger";
import { BugAds } from "./bug-ads";
import { BugAdsInfo, PauseAdsInfo } from "./app-api-config";
import { getFetchUrl } from "./models/data-service";

/**
 * Which overlay format a parser instance is fetching creatives for. Both formats
 * come off the same endpoint with the same VAST/creative-JSON shape, so they
 * share this parser and differ only in which adsdklist entry supplies the URL,
 * which actions are usable, and which app-level cache the result lands in.
 */
export type AdKind = 'bug' | 'pause';

export interface CreativeData {
    url: string;
    width: number;
    height: number;
    duration?: number;
    xpos?: number;
    ypos?: number;
    refWidth?: number;
    refHeight?: number;
}

export interface Tracker {
    start?: string;
    midpoint?: string;
    complete?: string;
}

export interface Creative {
    minSuggestedDuration?: string;
    url?: string;
    CreativeData?: CreativeData;
    trackers?: Tracker;
    Impression?: string;
    actions?: any;
    isLBand?: boolean;
}

export interface AdObj {
    adId?: string;
    AdSystem?: string;
    AdTitle?: string;
    Description?: string;
    Impression?: string | null;
    Creatives?: Creative[];
}

class VastParser {
    vastUrl: string = "";
    adObj: AdObj | null = null;
    bugAds: BugAds | null;
    config: BugAdsInfo | PauseAdsInfo | null = null;
    bugAdVastType: number = 3;
    kind: AdKind;

    constructor(_bugAds: BugAds | null = null, kind: AdKind = 'bug') {
        this.bugAds = _bugAds;
        this.kind = kind;
        this.init();
    }

    /**
     * Launch-time prefetch: fetch and parse the creatives for `kind` once and
     * cache them on the app so every video reuses the same data instead of
     * hitting the ad server on each eligible video. Safe to call more than once -
     * it no-ops if a fetch is already in flight or has completed. Fire-and-forget;
     * playback never waits on it.
     */
    static prefetch = (kind: AdKind = 'bug') => {
        const cache = VastParser.getCache(kind);
        if (!cache || cache.status === 'loading' || cache.status === 'ready') {
            return;
        }
        Log.info('adlog', 'VastParser.prefetch() - fetching ' + kind + ' ads at launch', '');
        cache.status = 'loading';
        new VastParser(null, kind);
    }

    // The app-level creative cache belonging to an ad kind. Each format keeps its
    // own so one format's empty/error result never suppresses the other.
    static getCache = (kind: AdKind) => {
        const { appState } = AppData as AppSetup;
        const app = appState?.getApp();
        return kind === 'pause' ? app?.pauseAdsCache : app?.bugAdsCache;
    }

    getCache = () => VastParser.getCache(this.kind);

    /*************************************************
    * initialise various params of ad playback
    **************************************************/
    init = () => {
        const { appState } = AppData as AppSetup;
        const adsdkConfig = appState?.getAppApiConfig()?.adsdkConfig;
        this.config = (this.kind === 'pause' ? adsdkConfig?.pauseAds : adsdkConfig?.bugAds) || null;

        if (!this.config?.adList?.url) {
            Log.info('adlog', 'vastParser.init() - No adList URL found for kind ', this.kind);
            this.markCache('empty');
            return false;
        }

        let url = getFetchUrl(this.config.adList.url);

        if (url == "") {
            this.markCache('empty');
            return false;
        } else {
            this.vastUrl = url;
            if (this.vastUrl.indexOf('v2.0') !== -1) {
                this.bugAdVastType = 2;
            } else {
                this.bugAdVastType = 3;
            }
            this.fetchAd();
            return true;
        }
    }

    /*************************************************
    * fetch vast based ads from the url specified
    **************************************************/
    fetchAd = () => {
        VastParser.fetchWithTimeout(this.vastUrl)
            .then(response => response.text())
            .then(async (data: string) => {
                if (this.bugAdVastType == 2) {
                    this.adObj = this.parseVastResponse(data);
                } else {
                    this.adObj = VastParser.parseMultiAdVastResponse(data);
                }

                if (this.adObj && this.adObj.Creatives !== undefined) {
                    const adObj = this.adObj;
                    const creatives = adObj.Creatives || [];

                    await Promise.all(
                        creatives.map((creative, i) =>
                            creative.url !== undefined ? this.parseActualAd(i, creative.url) : Promise.resolve()
                        )
                    );

                    adObj.Creatives = creatives.filter(
                        c => c.CreativeData !== undefined && this.isSupportedAction(c.actions)
                    );
                    Log.info('adlog', 'vastParser.fetchAd() - supported creatives: ', adObj);
                    if (adObj.Creatives.length === 0) {
                        if (this.bugAds) this.bugAds.shouldDisplayAd = false;
                        this.bugAds?.initAdLog(false, false);
                        this.markCache('empty');
                    } else {
                        this.storeCache(adObj);
                    }
                } else {
                    this.markCache('empty');
                }
            })
            .catch(() => {
                this.bugAds?.initAdLog(false, false);
                this.markCache('error');
            });
    }

    // Persist the parsed creatives on the app so all videos reuse them and the
    // rotation counter (ads.breakPointAdMapCounter) walks a stable array.
    storeCache = (adObj: AdObj) => {
        const cache = this.getCache();
        if (cache) {
            cache.adObj = adObj;
            cache.status = 'ready';
            Log.info('adlog', 'vastParser.storeCache() - cached ' + this.kind + ' ad creatives: ', adObj.Creatives);
        }
    }

    // Record a non-ready terminal state on the cache without clobbering
    // creatives that a previous successful fetch may have stored.
    markCache = (status: 'empty' | 'error') => {
        const cache = this.getCache();
        if (cache && cache.status !== 'ready') {
            cache.status = status;
        }
    }

    // Static (also called from row-model.ts for grid-banner-ad rows).
    static parseMultiAdVastResponse = (responseVast: any): AdObj | null => {
        var adObj: AdObj = {};
        var response = (responseVast instanceof Document ? responseVast : $.parseXML(responseVast));

        var ad = $(response).find("Ad");   // get the main ad container

        if (!ad || ad.length === 0)
            return null;

        //get ad id
        var adId = $(response).find("Ad")[0].id;
        if (adId !== undefined) {
            adObj.adId = adId;
        }

        //get ad system
        var AdSystem = $(ad).find('AdSystem').text();
        if (AdSystem !== undefined) {
            adObj.AdSystem = AdSystem;
        }

        //get ad name
        var AdTitle = $(ad).find('AdTitle').text();
        if (AdTitle !== undefined) {
            adObj.AdTitle = AdTitle;
        }

        //get ad description
        var Description = $(ad).find('Description').text();
        if (Description !== undefined) {
            adObj.Description = Description;
        }

        //get ad creatives
        var Creative = $(ad).find('Creative');
        if (Creative !== undefined && Creative.length > 0) {
            adObj.Creatives = [];
            $(Creative).each(function (i: number, val: any) {
                if (adObj.Creatives) {
                    adObj.Creatives[i] = {};
                    adObj.Creatives[i].minSuggestedDuration = $(this).find('minSuggestedDuration').text();
                    adObj.Creatives[i].url = $(this).find('IFrameResource').text();

                    var trackers = $(this).find('Tracking');
                    if (trackers !== undefined && trackers.length > 0) {
                        adObj.Creatives[i].trackers = {};
                        if ($(this).find('Impression').length > 0) {
                            adObj.Creatives[i].Impression = $(this).find('Impression').text();
                        }
                        $(trackers).each(function (i2: number, val2: any) {
                            if ($(this).attr('event') == 'start') {
                                adObj.Creatives![i].trackers!.start = $(this).text();
                            } else if ($(this).attr('event') == 'midpoint') {
                                adObj.Creatives![i].trackers!.midpoint = $(this).text();
                            } else if ($(this).attr('event') == 'complete') {
                                adObj.Creatives![i].trackers!.complete = $(this).text();
                            }
                        });
                    }
                }
            });
            adObj.Impression = null;
        }

        return adObj;

    }

    parseVastResponse = (responseVast: any): AdObj | null => {

        var adObj: AdObj = {};
        var response = (responseVast instanceof Document ? responseVast : $.parseXML(responseVast));

        var ad = $(response).find("Ad");   // get the main ad container

        if (!ad || ad.length === 0)
            return null;

        //get ad id
        var adId = $(response).find("Ad")[0].id;
        if (adId !== undefined) {
            adObj.adId = adId;
        }

        //get ad system
        var AdSystem = $(ad).find('AdSystem').text();
        if (AdSystem !== undefined) {
            adObj.AdSystem = AdSystem;
        }

        //get ad name
        var AdTitle = $(ad).find('AdTitle').text();
        if (AdTitle !== undefined) {
            adObj.AdTitle = AdTitle;
        }

        //get ad description
        var Description = $(ad).find('Description').text();
        if (Description !== undefined) {
            adObj.Description = Description;
        }

        //get ad impression
        var Impression = $(ad).find('Impression').text();
        if (Impression !== undefined) {
            adObj.Impression = Impression;
        }

        //get ad creatives
        var Creative = $(ad).find('Creative');
        if (Creative !== undefined && Creative.length > 0) {
            adObj.Creatives = [];
            $(Creative).each(function (i: number, val: any) {
                if (adObj.Creatives) {
                    adObj.Creatives[i] = {};
                    adObj.Creatives[i].minSuggestedDuration = $(this).find('minSuggestedDuration').text();
                    adObj.Creatives[i].url = $(this).find('IFrameResource').text();

                    var trackers = $(this).find('Tracking');
                    if (trackers !== undefined && trackers.length > 0) {
                        adObj.Creatives[i].trackers = {};
                        $(trackers).each(function (i2: number, val2: any) {
                            if ($(this).attr('event') == 'start') {
                                adObj.Creatives![i].trackers!.start = $(this).text();
                            } else if ($(this).attr('event') == 'midpoint') {
                                adObj.Creatives![i].trackers!.midpoint = $(this).text();
                            } else if ($(this).attr('event') == 'complete') {
                                adObj.Creatives![i].trackers!.complete = $(this).text();
                            }
                        });
                    }
                }
            });
            adObj.Impression = Impression;
        }

        // Log.info('adlog', 'vastParser.parseVastResponse() - adObj: ', adObj);
        return adObj;

    }

    // Pick the best pre-calibrated variant for the current screen width.
    // fhd is designed for 1920-wide, hd for 1280-wide, sd for 720-wide.
    // Static (also called from row-model.ts for grid-banner-ad rows).
    static selectCreativeVariant = (info: any): { raw: any; refWidth: number; refHeight: number } | null => {
        const w = window.innerWidth;
        if (w >= 1280 && info.fhd) return { raw: info.fhd, refWidth: 1920, refHeight: 1080 };
        if (w >= 720  && info.hd)  return { raw: info.hd,  refWidth: 1280, refHeight: 720  };
        if (info.sd)               return { raw: info.sd,  refWidth: 720,  refHeight: 405  };
        const fallback = info.fhd || info.hd || info.sd;
        return fallback ? { raw: fallback, refWidth: 1920, refHeight: 1080 } : null;
    }

    /**
     * Mirror the creative's own duration onto the config as this kind's fallback,
     * so an overlay whose creative arrives without one still auto-dismisses on a
     * server-driven timing rather than the hardcoded default.
     */
    setDefaultDuration = (duration: number) => {
        if (!this.config || isNaN(duration)) return;
        if (this.kind === 'pause') {
            (this.config as PauseAdsInfo).pauseadduration = duration;
        } else {
            (this.config as BugAdsInfo).bugadduration = duration;
        }
    }

    // Normalise the raw variant object into a typed CreativeData.
    // The API uses w/h (strings) while our interface expects width/height (numbers).
    // Static (also called from row-model.ts for grid-banner-ad rows).
    static buildCreativeData = (raw: any, refWidth: number, refHeight: number, duration: number): CreativeData => {
        return {
            url:       raw.url,
            width:     parseInt(raw.w  ?? raw.width  ?? 0),
            height:    parseInt(raw.h  ?? raw.height ?? 0),
            xpos:      raw.xpos  !== undefined ? parseInt(raw.xpos)  : undefined,
            ypos:      raw.ypos  !== undefined ? parseInt(raw.ypos)  : undefined,
            duration,
            refWidth,
            refHeight,
        };
    }

    // Tagged per kind so each format's deInit only drops its own preload hints.
    prefetchCreative = (imgUrl: string) => {
        const tag = this.kind === 'pause' ? 'data-pausead' : 'data-bugad';
        try {
            $('head').append('<link rel="preload" href="' + imgUrl + '" as="image" ' + tag + '="true">')
        } catch (e) { }
    }

    // Shared fetch for all bug-ad-shaped API calls (bug/pause ads and, via
    // row-model.ts, grid-banner-ad rows): enforces a hard timeout so a hung ad
    // server can't leak a pending request on TV devices, and rejects non-2xx
    // responses so error pages never reach the XML/JSON parsers. Any failure
    // lands in the caller's .catch, keeping playback/rendering untouched.
    static fetchWithTimeout = (url: string, timeoutMs = 5000): Promise<Response> => {
        const controller = new AbortController();
        const timer = setTimeout(() => controller.abort(), timeoutMs);
        return fetch(url, { signal: controller.signal })
            .then(response => {
                if (!response.ok) throw new Error('bugad request failed: ' + response.status);
                return response;
            })
            .finally(() => clearTimeout(timer));
    }

    // A bug-ad creative is only kept if its action is one the app can handle.
    static SUPPORTED_ACTIONS = ['openViaDeeplink', 'launchInAppOpening', 'addToFavorite'];

    // Pause-ad creatives carry their own, separate action set.
    static SUPPORTED_PAUSE_ACTIONS = ['paidSubscription'];

    static L_BAND_ACTIONS = ['addToFavorite'];

    getSupportedActions = (): string[] => {
        return this.kind === 'pause' ? VastParser.SUPPORTED_PAUSE_ACTIONS : VastParser.SUPPORTED_ACTIONS;
    }

    /**
     * True when the creative JSON's actions contain at least one type this kind
     * can act on. The two formats name the action in different places - bug ads
     * in the key with a parameter object as the value
     * (`{"launchInAppOpening": {"siteid": 1}}`), pause ads in the value keyed by
     * the key press that triggers it (`{"enter": "paidSubscription"}`) - so both
     * positions are matched. Only string values are considered, which keeps a
     * bug-ad parameter object from ever matching by accident.
     */
    isSupportedAction = (actions: any): boolean => {
        if (!actions) return false;
        const supported = this.getSupportedActions();
        return Object.keys(actions).some(k => supported.indexOf(k) !== -1
            || (typeof actions[k] === 'string' && supported.indexOf(actions[k]) !== -1));
    }

    /**
     * The action name a pause-ad creative wants run, read out of the value side
     * of its actions object. Returns null when it serves nothing actionable.
     */
    static getPauseActionName = (actions: any): string | null => {
        if (!actions) return null;
        const key = Object.keys(actions).find(k => typeof actions[k] === 'string'
            && VastParser.SUPPORTED_PAUSE_ACTIONS.indexOf(actions[k]) !== -1);
        return key ? actions[key] : null;
    }

    // True when the creative JSON's actions mark it as an L-band.
    static isLBandAction = (actions: any): boolean => {
        if (!actions) return false;
        return Object.keys(actions).some(k => VastParser.L_BAND_ACTIONS.indexOf(k) !== -1);
    }

    /*********************************************************************
    * parse actual ad response which contains the creative details
    **********************************************************************/
    // For multi bug ads
    parseActualAd = (index: number, url: string): Promise<void> => {
        if (this.bugAdVastType == 2) {
            return this.parseActualAdSingle(url);
        }
        // Log.info('adlog', 'vastParser.parseActualAd() - url: ', url);
        return VastParser.fetchWithTimeout(getFetchUrl(url))
            .then(response => response.json())
            .then((actualAd: any) => {
                // Log.info('adlog', 'vastParser.parseActualAd() - data: ', actualAd);
                const creative = this.adObj?.Creatives?.[index];
                if (actualAd?.info !== undefined && creative) {
                    // Log.info('adlog', 'vastParser.parseActualAd() - actualAd parsed: ', actualAd);

                    if (!this.isSupportedAction(actualAd.actions)) {
                        // Log.info('adlog', 'vastParser.parseActualAd() - unsupported/missing action, skipping index ', index);
                        return;
                    }
                    const variant = VastParser.selectCreativeVariant(actualAd.info);
                    if (!variant) {
                        // Log.info('adlog', 'vastParser.parseActualAd() - no creative variant, skipping index ', index);
                        return;
                    }
                    const duration = parseInt(actualAd.info.duration);
                    creative.CreativeData = VastParser.buildCreativeData(variant.raw, variant.refWidth, variant.refHeight, duration);
                    this.prefetchCreative(creative.CreativeData.url);
                    this.setDefaultDuration(duration);
                    creative.actions = actualAd.actions;
                    creative.isLBand = VastParser.isLBandAction(actualAd.actions);
                }
            })
            .catch(() => {
                // Multi-ad: a single creative failing must not disable the whole
                // feature — other creatives may have loaded. Skip only this one;
                // it is filtered out in fetchAd.
                Log.info('adlog', 'vastParser.parseActualAd() - creative fetch failed, skipping index ', index);
                this.bugAds?.initAdLog(false, false);
            });
    }

    parseActualAdSingle = (url: string): Promise<void> => {
        return VastParser.fetchWithTimeout(getFetchUrl(url))
            .then(response => response.json())
            .then((actualAd: any) => {
                const creative = this.adObj?.Creatives?.[0];
                if (actualAd?.info !== undefined && creative) {

                    if (!this.isSupportedAction(actualAd.actions)) {
                        return;
                    }
                    const variant = VastParser.selectCreativeVariant(actualAd.info);
                    if (!variant) {
                        Log.info('adlog', 'vastParser.parseActualAdSingle() - no creative variant, skipping', '');
                        return;
                    }
                    const duration = parseInt(actualAd.info.duration);
                    creative.CreativeData = VastParser.buildCreativeData(variant.raw, variant.refWidth, variant.refHeight, duration);
                    this.prefetchCreative(creative.CreativeData.url);
                    this.setDefaultDuration(duration);
                    creative.actions = actualAd.actions;
                    creative.isLBand = VastParser.isLBandAction(actualAd.actions);
                }
            })
            .catch(() => {
                if (this.bugAds) this.bugAds.shouldDisplayAd = false;
                this.bugAds?.initAdLog(false, false);
            });
    }
}

export default VastParser;
